import { Link } from "@tanstack/react-router";
import { Search } from "lucide-react";
import { useMemo } from "react";
import { BrowseCategories } from "@/components/discovery/BrowseCategories";
import { Recommendations } from "@/components/discovery/Recommendations";
import { TopSkills } from "@/components/discovery/TopSkills";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useSkillCatalog } from "@/hooks/use-skills";
import { facetCounts } from "@/lib/skills/catalog";

/** The curated Discover home: recommendations + Top Skills + category browse cards. */
export function DiscoveryView() {
  const { data: skills, isLoading, isError, refetch } = useSkillCatalog();
  const all = skills ?? [];
  const counts = useMemo(() => facetCounts(all), [all]);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-[13px] font-semibold uppercase tracking-[0.14em] text-fg-link">
            Internal Library
          </div>
          <h1 className="mt-1 font-display text-[30px] font-bold text-fg-1">
            Discover Claude Skills
          </h1>
          <p className="mt-2 max-w-2xl text-fg-2">
            A central, vetted library of Claude skills — start from a scenario, see what your team
            installs most, or browse by category.
          </p>
        </div>
        <Button asChild variant="outline" size="app">
          <Link to="/skills">
            <Search className="size-4" />
            Search all skills
          </Link>
        </Button>
      </div>

      {isLoading && (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-44 rounded-lg" />
          ))}
        </div>
      )}

      {isError && <ErrorState onRetry={() => void refetch()} />}

      {!isLoading && !isError && all.length > 0 && (
        <>
          <Recommendations skills={all} />
          <TopSkills skills={all} />
          <BrowseCategories counts={counts} total={all.length} />
        </>
      )}
    </div>
  );
}
